import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  getSessionMessages,
  intelCardsOf,
  interruptTurn,
  openSession,
  sendMessageStream,
  uploadFiles,
  type Attachment,
  type Card,
  type ChatMessage,
  type Thought,
} from '@/services/api';
import {
  answerable,
  liveOfferAt,
  lookupNote,
  rowsWithOffers,
  type LookupKind,
  type OfferRow,
  type Staged,
} from './lookupGate';

// --- following a turn we are not streaming ----------------------------------
// A turn belongs to the SERVER, not to the fetch that started it. Refresh, close
// the tab, drop the wifi, open the same chat in a second tab — the agent keeps
// working and the answer lands in the transcript regardless. So whenever we find
// a session the server reports as running and we have no stream for it, we
// re-read the transcript until it settles.
//
// ponytail: polling the whole transcript, not a cheap status endpoint. Every
// tick before the last one is wasted work (the transcript only changes at the
// end), but it needs no new route and the usual wait is well under a minute.
//
// A cheaper `GET /api/sessions/{id}/status` is fine to add — but it must answer
// from the TRANSCRIPT, the way this endpoint does. The platform's own session
// status is not the same question: it reports idle between the steps of one turn
// (measured flapping 8s into a 20s turn, between two tool calls), so a poll built
// on it samples a gap and declares the turn finished — which is precisely the
// empty-looking chat this whole mechanism exists to prevent.
const POLL_MS = 4000;
// ~15 minutes. A ceiling, not a timeout the user should ever reach: it exists so
// a session the platform reports as running forever can't poll for the lifetime
// of the tab.
const POLL_MAX_TICKS = 225;
/** Status shown while following someone else's stream (or our own, after the
 *  connection went). Deliberately not "Thinking…": we genuinely don't know what
 *  the agent is doing — the narration lived on the stream we lost. */
const RESUMED_STATUS = 'Still working…';
// Shown, and kept, while following a turn the server refused to stop.
const STOP_FAILED_STATUS = "Couldn't stop Elliot — it's still working on this. Its answer will appear here.";

// --- self-healing a turn whose stream died silently -------------------------
// A turn ends in the UI when its stream delivers a terminal frame (done/error)
// or the reader ends (→ onInterrupted → watchTurn). If instead the reader is
// abandoned WITHOUT resolving — a page torn down under dev HMR, a proxy holding a
// dead socket open — none of those fire, and the convo sits 'working' forever.
// The server heart-beats an open stream every ~20s (STREAM_HEARTBEAT_SECS), and
// every byte, heartbeat included, refreshes `lastSeen`; so silence this long is
// proof the stream is gone, not a merely quiet step. Then watchTurn reconciles
// from the transcript — exactly what a reload would do, read-only and idempotent.
export const STUCK_SILENCE_MS = 60_000;
const STUCK_CHECK_MS = 15_000;

/** Whether a convo is a turn whose stream has silently died: still 'working',
 *  already has an id to reconcile against, and heard nothing past the window.
 *  A pure seam so the watchdog's decision is testable without timers or fetch. */
export function isStuckWorking(
  convo: { phase: Phase; serverId: string | null },
  lastSeenAt: number | undefined,
  now: number,
): boolean {
  return (
    convo.phase === 'working' &&
    convo.serverId != null &&
    lastSeenAt != null &&
    now - lastSeenAt > STUCK_SILENCE_MS
  );
}

// --- stopping a turn for real --------------------------------------------------
// Stop used to stop LISTENING: it aborted the fetch and the agent ran on, cost
// and all, in the background. Worse, it re-enabled the composer while the turn
// was still alive, and the next question started a second turn on the same
// session — two server loops that each ran every tool call, which is how one
// live-scan screenshot got mounted twice and file delivery died for the rest of
// that chat (VIS-580). Stop now goes to the server (`interruptTurn`).
//
// The server can only stop what it can name. The stream hands over the turn's
// id (`turn_started`) once the message has actually reached the agent, and Stop
// sends it back, so a Stop that lands late can never hit the NEXT question.
// That leaves the moments before the id arrives — the server is still setting
// the turn up, and a Stop sent then finds nothing to stop while the turn starts
// a beat later. So a Stop pressed in that window is HELD: the chat shows
// "stopped" at once, the stream keeps being read (silently), and the Stop goes
// out the instant the id lands.
export type StopPlan = 'send' | 'hold' | 'send-untargeted' | 'none';

/** What the Stop button should do, from what this tab knows about the turn.
 *  A pure seam so the decision is testable without a stream, timers or fetch. */
export function stopPlan(t: {
  turnId: string | null;
  streaming: boolean;
  serverId: string | null;
}): StopPlan {
  if (t.turnId && t.serverId) return 'send';
  if (t.streaming) return 'hold'; // the id is on its way on this very stream
  // Following the turn by polling (after a reload): no stream, so no id will
  // ever come. Stopping "whatever holds this chat" is right — it is the turn on
  // screen.
  if (t.serverId) return 'send-untargeted';
  return 'none';
}

// --- surviving a refresh in the first second --------------------------------
// Between pressing Enter and the `session` frame there is a real window — one
// round trip — where the turn exists on the server and this browser cannot name
// it. Refresh in that window and the URL is still "/", so the whole conversation
// vanished from the screen while the agent worked on it. People re-sent the same
// question, which is two turns and two bills for one answer.
//
// So the send is written down before it goes out, and picked up again on boot.
// `sessionStorage`, not `localStorage`: this belongs to THIS tab's reload, and a
// brand-new tab must not adopt some other tab's turn.
const PENDING_KEY = 'vh:pendingTurn';
// Older than this and it is not a refresh, it is a tab that was left overnight.
const PENDING_TTL_MS = 10 * 60 * 1000;

/** What a reload can pick up: the session id if we got that far (else null —
 *  which still says "a turn was in flight", enough to go re-read the list) and
 *  the result tab that send opened, if any.
 *
 *  The TAB is here because nothing else records it early enough: the layout
 *  store is keyed by server id (which doesn't exist yet), and the `?tab=` param
 *  reaches the URL through a router transition the send races — measured live,
 *  a refresh ~200ms after send still saw a bare "/". The note is written
 *  synchronously before anything leaves the browser, so it is the one record a
 *  fast F5 cannot beat.
 *
 *  Exported (with its writers) so tests drive the real storage contract rather
 *  than a copy of the key and shape that can drift from it. */
export function readPendingTurn(): { id: string | null; tab: string | null } | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { id?: unknown; tab?: unknown; at?: unknown };
    if (typeof v.at !== 'number' || Date.now() - v.at > PENDING_TTL_MS) return null;
    return {
      id: typeof v.id === 'string' ? v.id : null,
      tab: typeof v.tab === 'string' ? v.tab : null,
    };
  } catch {
    return null; // storage-less browser, or a corrupt entry — nothing to resume
  }
}

export function writePendingTurn(id: string | null, tab: string | null = null): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ id, tab, at: Date.now() }));
  } catch {
    /* private mode / quota — a refresh just loses the hand-off */
  }
}

/** Annotate the in-flight send with the tab it opened. A no-op when no send is
 *  in flight: this only ever adds detail to an existing note — it must not
 *  invent one, or closing a tab hours later would look like an interrupted turn. */
export function notePendingTurnTab(tab: string): void {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;
    const v = JSON.parse(raw) as { id?: unknown; at?: unknown };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ id: v.id ?? null, tab, at: v.at }));
  } catch {
    /* same tolerance as the writer */
  }
}

export function clearPendingTurn(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* nothing to clear */
  }
}

// --- tab layout persistence -------------------------------------------------
// The right-panel tabs ARE the working layout, so reopening a chat reopens what
// was last open in it — including `search:` tabs, which re-run their AirQL.
// Keyed by MA session id; an unsaved convo has no stable identity to key on yet.
//
// WHICH tabs are open lives here. WHICH ONE you are looking at does NOT: that is a
// location, so the URL owns it (`?tab=` — see HomePage) and it is deliberately not
// persisted. Two owners for one value is exactly how they drift apart.
//
// ponytail: localStorage, not the server — per-browser is what this ticket needs,
// and it costs no endpoint, no network write per tab click, and no deploy. Move it
// into session metadata if it ever has to follow a user across machines.
/** Where one conversation's tab layout lives. Exported so tests and stories seed
 *  the same key the code reads, instead of a string that can drift from it. */
export const layoutKey = (sessionId: string) => `vh:tabs2:${sessionId}`;

/**
 * Drop the v1 (`vh:tabs:`) generation of stored layouts. Runs once, on import.
 *
 * A bug published one chat's `?tab=` onto the NEXT chat's URL; the pass after that
 * read it as a link, opened the tab in that conversation, and this store then SAVED
 * it — so a v1 entry can name a lookup its conversation never asked about, and it
 * came back on every visit. The code fix (the guard in HomePage's reconcile effect)
 * stops new ones, but nothing can tell a poisoned entry from a real one, so the
 * generation goes whole: everyone loses their tab arrangement once, which is cheap
 * next to a panel showing another conversation's IP.
 *
 * `vh:tabs2:` does NOT match the v1 prefix — the 8th character is `2`, not `:` — so
 * this cannot eat the generation it is migrating to. Exported for that assertion.
 */
export function sweepV1Layouts(): void {
  try {
    // `length` + `key(i)` is Storage's defined enumeration API — `Object.keys` only
    // works on it because a real Storage is an exotic object that mirrors its keys
    // as properties. Backwards, because removing an entry shifts the indices above
    // it and a forward loop would skip its neighbour.
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k?.startsWith('vh:tabs:')) localStorage.removeItem(k);
    }
  } catch {
    /* storage-less browser / private mode — nothing to sweep */
  }
}

// Before the first `loadLayout`, exactly once, and not React's business.
sweepV1Layouts();

const NO_TABS: string[] = [];
const NO_THOUGHTS: Thought[] = [];
const NO_OFFERS: Staged[] = [];
const NO_MESSAGES: ChatMessage[] = [];

// --- the lookup gate's offers, across a reload ------------------------------
// An offer has no server-side row — nothing was sent to the agent, which is the
// point — so it is kept here beside the tab layout, for the same reasons
// (per-browser, no endpoint, no network write per keystroke) and keyed the same
// way, by session id.
//
// There IS a session to key on: a gated lookup creates the conversation (and
// titles it) the moment the user sends, holding back only the agent turn. That
// is what keeps the sidebar row, the generated name and `/chat/<id>` behaving
// exactly as they did before the gate.
//
// ponytail: the one gap is a refresh inside the round trip that fetches the id
// (measured well under a second) — there is no key to write under yet, so the
// offer is lost and the resume note brings the user back to the chat without it.
// Persist under the client key too if that is ever more than a curiosity.
/** Where one conversation's un-sent offers live. Exported so tests and stories
 *  seed the same key the code reads. */
const offersKey = (sessionId: string) => `vh:offers:${sessionId}`;

/** The offers a conversation still owes an answer on. Never throws — a corrupt
 *  entry or a storage-less browser just means the gate starts empty. */
function loadOffers(sessionId: string): Staged[] {
  let parsed: unknown;
  try {
    const raw = localStorage.getItem(offersKey(sessionId));
    if (!raw) return NO_OFFERS;
    parsed = JSON.parse(raw);
  } catch {
    return NO_OFFERS;
  }
  const v = parsed as { staged?: unknown } | null;
  if (!v || !Array.isArray(v.staged)) return NO_OFFERS;
  const staged = v.staged.filter(
    (x): x is Staged =>
      typeof x === 'object' && x !== null &&
      typeof (x as Staged).query === 'string' && (x as Staged).query !== '' &&
      ['ip', 'domain', 'search'].includes((x as Staged).kind),
  );
  return staged.length ? staged : NO_OFFERS;
}

/** Mirror of `saveLayout`: an empty list removes the entry, because draining the
 *  offers is exactly as meaningful as having them. */
function saveOffers(sessionId: string, staged: Staged[]): void {
  try {
    if (staged.length) {
      localStorage.setItem(offersKey(sessionId), JSON.stringify({ staged }));
    } else {
      localStorage.removeItem(offersKey(sessionId));
    }
  } catch {
    /* quota / private mode — the offers just don't survive the reload */
  }
}

/** The tabs a session was last used with, in order — or none. Never throws: a
 *  corrupt entry, a full disk or a storage-less browser just means it opens closed. */
export function loadLayout(sessionId: string): string[] {
  let parsed: unknown;
  try {
    const raw = localStorage.getItem(layoutKey(sessionId));
    if (!raw) return NO_TABS;
    parsed = JSON.parse(raw);
  } catch {
    return NO_TABS;
  }
  const v = parsed as { targets?: unknown } | null;
  if (!v || !Array.isArray(v.targets)) return NO_TABS;
  const targets = v.targets.filter((t): t is string => typeof t === 'string');
  return targets.length ? targets : NO_TABS;
}

/**
 * Which tab is on screen, given the open ones and whatever `?tab=` says.
 *
 * The URL wins when it names a tab that is actually open. Everything else — no
 * `?tab=` at all, a link whose tab this browser has never opened, a tab that was
 * just closed — falls back to the LAST tab, one rule for every case. Lives here so
 * the fallback is written once: HomePage uses it both to render and to pick the tab
 * the URL moves to after a close.
 */
export function visibleTab(targets: string[], fromUrl: string | null): string | null {
  if (fromUrl && targets.includes(fromUrl)) return fromUrl;
  return targets[targets.length - 1] ?? null;
}

/** What has to happen to make the URL and the open tabs agree — see `tabIntent`. */
export type TabIntent =
  | { act: 'none' }
  | { act: 'open'; target: string } // the URL names a tab we don't have → create it
  | { act: 'url'; target: string | null }; // write (or clear) `?tab=`

const NO_ACT: TabIntent = { act: 'none' };

/**
 * The `?tab=` policy, as one decision instead of an effect full of branches.
 *
 * The URL owns which tab is visible, but it can be ahead of the app (a link opened
 * where the tab layout isn't — another browser, another device, a cleared profile)
 * or behind it (a leftover from the chat you just left). Both bugs found by hand
 * during the router work lived in the ORDER of those two, so they're a table here
 * rather than a chain of ifs inside a hook.
 */
export function tabIntent(o: {
  inChat: boolean; // the URL points at a chat (`/chat/:id`) — not necessarily loaded
  hasConversation: boolean; // a convo is on screen (a brand-new one has no id yet)
  targets: string[]; // its open tabs
  fromUrl: string | null; // `?tab=`
  // Was `fromUrl` open on the previous pass? Then it isn't a link, it's the URL
  // lagging a close — see the branch below. Absent = no, treat it as a link.
  wasOpen?: boolean;
  // A tab was just RENAMED (editTarget: `from` → `to`, same position). `?tab=`
  // still names `from` for one render — the router applies the url change in a
  // transition — so `from` has vanished from `targets` while `to` has appeared.
  // Without this the `wasOpen` branch below reads that as a CLOSE and jumps the
  // panel to another tab; map the stale url onto the new key instead.
  renamed?: { from: string; to: string };
}): TabIntent {
  if (!o.hasConversation) {
    // A chat named in the URL just hasn't loaded yet: leave the URL alone, or we'd
    // eat the tab the link carried before it had anywhere to go.
    if (o.inChat) return NO_ACT;
    // Otherwise there is no chat and none coming — the tab is a leftover.
    return o.fromUrl ? { act: 'url', target: null } : NO_ACT;
  }
  if (o.fromUrl) {
    if (o.targets.includes(o.fromUrl)) return NO_ACT;
    // A rename in flight, not a close: the url still names the old key but its tab
    // lives on under the new one. Catch the url up to it rather than treating the
    // old key as gone — which the `wasOpen` branch just below would, jumping the
    // panel elsewhere. Must sit before `wasOpen`, because a rename is also "open a
    // moment ago".
    if (o.renamed && o.fromUrl === o.renamed.from && o.targets.includes(o.renamed.to)) {
      return { act: 'url', target: o.renamed.to };
    }
    // `?tab=` can be BEHIND the tabs, not ahead of them: react-router applies a
    // navigation inside startTransition, so the render right after a tab closes
    // still carries the closed tab's `?tab=`. Reading that as "open this" is what
    // re-created the tab — which is why closing one worked only sometimes, and why
    // a panel with several tabs could not be closed at all. A tab that was open a
    // moment ago is that lag: catch the URL up instead of re-creating the tab.
    if (o.wasOpen) return { act: 'url', target: visibleTab(o.targets, null) };
    // The layout is per-browser, so the URL is allowed to create a tab it names —
    // that's what makes the link work at all on a machine with no layout stored.
    return { act: 'open', target: o.fromUrl };
  }
  // No `?tab=`: publish the restored layout's tab, so what's on screen is always in
  // the URL and copying the address bar captures it.
  const last = visibleTab(o.targets, null);
  return last ? { act: 'url', target: last } : NO_ACT;
}

/** Persists only which tabs are open — see the note above `layoutKey`. */
export function saveLayout(sessionId: string, targets: string[]): void {
  try {
    if (targets.length) {
      localStorage.setItem(layoutKey(sessionId), JSON.stringify({ targets }));
    } else {
      localStorage.removeItem(layoutKey(sessionId)); // closing every tab is a choice too
    }
  } catch {
    /* quota / private mode — the layout just doesn't survive the reload */
  }
}

let keyCounter = 0;
const nextKey = () => `c-${Date.now()}-${keyCounter++}`;
let msgCounter = 0;
const nextId = (p: string) => `${p}-${Date.now()}-${msgCounter++}`;

/**
 * One conversation's lifecycle. The UI is a pure function of `phase`, so it
 * always knows what to show — and turns update only their OWN convo (by key),
 * regardless of which one is on screen, so navigating never desyncs state.
 *   loading  — fetching the saved transcript
 *   working  — a turn is in flight (status carries the live label)
 *   idle     — done / loaded, showing messages
 *   error    — the turn or load failed (error carries the message)
 *   stopped  — the user stopped the turn
 */
export type Phase = 'loading' | 'working' | 'idle' | 'error' | 'stopped';

// The agents a chat can run on — sent as the `mode` field of /api/message/stream.
// The server writes it into session metadata at creation and treats it as
// IMMUTABLE afterwards (server/models.py), which is exactly the product rule: a
// chat that started on one agent stays on it.
export const AGENT_MODES = ['Recon', 'Hunter'] as const;
export type AgentMode = (typeof AGENT_MODES)[number];
export const DEFAULT_AGENT_MODE: AgentMode = 'Recon';

/** The line under each agent name in the picker: what the agent is FOR, in the
 *  user's terms rather than ours. Keyed by AgentMode and kept beside AGENT_MODES
 *  on purpose — adding an agent then cannot ship a row with a blank second line. */
export const AGENT_BLURB: Record<AgentMode, string> = {
  Recon: 'Fastest for quick lookups',
  Hunter: 'For your toughest investigations',
};

/** Narrow an arbitrary stored/served mode string back to a known agent. */
export function toAgentMode(mode?: string | null): AgentMode {
  return (AGENT_MODES as readonly string[]).includes(mode ?? '')
    ? (mode as AgentMode)
    : DEFAULT_AGENT_MODE;
}

export interface Convo {
  key: string; // stable client key (never changes)
  serverId: string | null; // MA session id, once known
  isNew: boolean; // optimistic — created here, not yet in the saved list
  caseId: string | null; // case it's composed in (for the optimistic sidebar row)
  mode: AgentMode; // the agent this chat runs on — fixed at creation, never changes
  phase: Phase;
  status: string | null; // human label while working
  title: string | null; // null = title still loading (new chats)
  error: string | null;
  // Set only for a model-outage error ('model_outage'): the session's model is
  // down but a NEW chat works (the backend creates it on a fallback model), so
  // the error row offers one. null for every other error.
  errorKind: 'model_outage' | null;
  messages: ChatMessage[];
  // The interim narration of the turn IN FLIGHT, accumulating as it streams.
  // Folded into the answer bubble's `thoughts` when the turn lands, then cleared;
  // shown live (a "thinking" timeline) while `working`.
  liveThoughts: Thought[];
  // Lookups the user has entered that were OFFERED and never sent to the agent,
  // oldest first (see features/chat/lookupGate.ts). They accumulate rather than
  // replace, which is what lets "are they connected?" reach the agent knowing
  // which indicators "they" are; any turn drains the list, carrying whatever it
  // did not send itself as a hidden note. Deliberately NOT in `messages`: an
  // offer has no server-side row, so every transcript read would wipe it —
  // `rowsWithOffers` composes the two at render time instead.
  staged: Staged[];
  targets: string[]; // lookup targets opened in THIS conversation, as "kind:value" keys (e.g. "ip:8.8.8.8", "domain:google.com") — right-panel tabs
  // No `activeTarget` here on purpose: which tab you're looking at is a location and
  // lives in the URL (`?tab=`). State holding it too would be a second owner to keep
  // in sync — see HomePage, which derives it and writes it.
}

export interface State {
  convos: Record<string, Convo>;
  currentKey: string | null; // the convo on screen
}

export type Action =
  | { t: 'open'; convo: Convo } // create + view
  | { t: 'view'; key: string | null }
  | { t: 'patch'; key: string; patch: Partial<Convo> }
  | { t: 'appendMsg'; key: string; message: ChatMessage }
  | { t: 'addThought'; key: string; thought: Thought }
  | { t: 'patchMsg'; key: string; id: string; patch: Partial<ChatMessage> }
  | { t: 'addOffer'; key: string; offer: Staged } // the gate asked; nothing sent yet
  | { t: 'declineOffer'; key: string } // "no" to the newest live offer — kept as context
  | { t: 'dropOffer'; key: string; index: number } // confirmed: it became a real message
  | { t: 'markOffersSent'; key: string } // a turn carried their context to the agent
  | { t: 'markSaved'; serverId: string } // optimistic entry is now in the saved list
  | { t: 'openTarget'; target: string } // add a target tab to the current convo
  | { t: 'closeTarget'; target: string }
  | { t: 'closeAllTargets' } // close the panel — one action, see the reducer case
  | { t: 'editTarget'; oldTarget: string; newTarget: string }; // rename a tab in place (e.g. edited search query)

export function reducer(state: State, a: Action): State {
  switch (a.t) {
    case 'open':
      return { convos: { ...state.convos, [a.convo.key]: a.convo }, currentKey: a.convo.key };
    case 'view':
      return { ...state, currentKey: a.key };
    case 'patch': {
      const c = state.convos[a.key];
      if (!c) return state;
      return { ...state, convos: { ...state.convos, [a.key]: { ...c, ...a.patch } } };
    }
    case 'appendMsg': {
      const c = state.convos[a.key];
      if (!c) return state;
      return {
        ...state,
        convos: { ...state.convos, [a.key]: { ...c, messages: [...c.messages, a.message] } },
      };
    }
    case 'addThought': {
      const c = state.convos[a.key];
      if (!c) return state;
      return {
        ...state,
        convos: { ...state.convos, [a.key]: { ...c, liveThoughts: [...c.liveThoughts, a.thought] } },
      };
    }
    case 'patchMsg': {
      const c = state.convos[a.key];
      if (!c) return state;
      return {
        ...state,
        convos: {
          ...state.convos,
          [a.key]: { ...c, messages: c.messages.map((m) => (m.id === a.id ? { ...m, ...a.patch } : m)) },
        },
      };
    }
    case 'addOffer': {
      const c = state.convos[a.key];
      if (!c) return state;
      // Entering the SAME indicator again while its question is the one on
      // screen changes nothing — the question is already there, with its
      // buttons. Without this, typing an IP three times gave three identical
      // exchanges in the chat while the result panel showed one tab, because
      // `openTarget` has always ignored a target it already has. Two views of
      // one action should not disagree about how many times it happened.
      //
      // Only against the LIVE one: an indicator that was asked about, walked
      // past, and then raised again IS a new question, and re-staging it is how
      // it becomes answerable again.
      const last = c.staged[c.staged.length - 1];
      if (last && answerable(last) && last.query === a.offer.query && last.kind === a.offer.kind) {
        return state;
      }
      return {
        ...state,
        convos: { ...state.convos, [a.key]: { ...c, staged: [...c.staged, a.offer] } },
      };
    }
    // Only the NEWEST live offer is answerable (it is the only one with buttons),
    // so "no" always means that one. It stays in the list: declining says "not
    // that lookup", never "forget I mentioned it", and a later question still has
    // to reach the agent knowing what was on screen.
    case 'declineOffer': {
      const c = state.convos[a.key];
      if (!c) return state;
      const at = liveOfferAt(c.staged);
      if (at === -1) return state;
      return {
        ...state,
        convos: {
          ...state.convos,
          [a.key]: {
            ...c,
            staged: c.staged.map((s, i) => (i === at ? { ...s, declined: true } : s)),
          },
        },
      };
    }
    /**
     * The user said yes, so this exchange is CONSUMED: its indicator is about to
     * be sent as the turn's own message, and the answer lands under it. Keeping
     * the row as well showed the indicator twice in a row — once as the
     * exchange's bubble, once as the real message — live and again after every
     * reload, because the transcript legitimately holds that message now.
     *
     * The question bubble goes with it. That is the right history: the user
     * asked about an IP and got an answer, which is exactly what the
     * conversation looks like when no gate stood in the way.
     */
    case 'dropOffer': {
      const c = state.convos[a.key];
      if (!c || a.index < 0 || a.index >= c.staged.length) return state;
      return {
        ...state,
        convos: {
          ...state.convos,
          [a.key]: { ...c, staged: c.staged.filter((_, i) => i !== a.index) },
        },
      };
    }
    // A turn has carried every un-sent exchange to the agent in its note. They
    // are NOT discarded: they are the conversation's history, and the user
    // watched them happen. They only stop being answerable, and stop being
    // repeated in the next note.
    case 'markOffersSent': {
      const c = state.convos[a.key];
      if (!c || c.staged.every((x) => x.sent)) return state;
      return {
        ...state,
        convos: {
          ...state.convos,
          [a.key]: { ...c, staged: c.staged.map((x) => (x.sent ? x : { ...x, sent: true })) },
        },
      };
    }
    case 'markSaved': {
      const entry = Object.values(state.convos).find((c) => c.serverId === a.serverId && c.isNew);
      if (!entry) return state;
      return { ...state, convos: { ...state.convos, [entry.key]: { ...entry, isNew: false } } };
    }
    // The three tab actions only ever touch the LIST. Focus is the URL's job, so
    // it stays referentially stable here and never fights a navigation.
    case 'openTarget': {
      const c = state.currentKey ? state.convos[state.currentKey] : null;
      if (!c || c.targets.includes(a.target)) return state;
      return { ...state, convos: { ...state.convos, [c.key]: { ...c, targets: [...c.targets, a.target] } } };
    }
    case 'closeTarget': {
      const c = state.currentKey ? state.convos[state.currentKey] : null;
      if (!c || !c.targets.includes(a.target)) return state;
      return {
        ...state,
        convos: { ...state.convos, [c.key]: { ...c, targets: c.targets.filter((x) => x !== a.target) } },
      };
    }
    // Closing the PANEL is its own intent, not a loop over closeTarget. As a loop,
    // the caller had to pick the next `?tab=` from the list it was in the middle of
    // emptying, and named a sibling that was closing too — which `tabIntent` then
    // re-created, so a panel with 2+ tabs could not be closed at all.
    case 'closeAllTargets': {
      const c = state.currentKey ? state.convos[state.currentKey] : null;
      if (!c || c.targets.length === 0) return state;
      return { ...state, convos: { ...state.convos, [c.key]: { ...c, targets: [] } } };
    }
    case 'editTarget': {
      const c = state.currentKey ? state.convos[state.currentKey] : null;
      if (!c || !c.targets.includes(a.oldTarget) || a.newTarget === a.oldTarget) return state;
      // Rename in place, keeping the tab's position; if the new key already exists,
      // drop the old duplicate instead of listing it twice.
      const targets = c.targets.includes(a.newTarget)
        ? c.targets.filter((x) => x !== a.oldTarget)
        : c.targets.map((x) => (x === a.oldTarget ? a.newTarget : x));
      return { ...state, convos: { ...state.convos, [c.key]: { ...c, targets } } };
    }
  }
}

interface UseChat {
  sessionId: string | null; // the viewed convo's server id
  messages: ChatMessage[];
  /** The in-flight turn's interim narration, accumulating as it streams. */
  liveThoughts: Thought[];
  status: string | null;
  sending: boolean;
  error: string | null;
  /** 'model_outage' when the current error is the model being down — the UI
   *  offers "Start a new chat" (new chats run on a fallback model). */
  errorKind: 'model_outage' | null;
  hasConversation: boolean;
  loadingConversation: boolean;
  stopped: boolean;
  /** Optimistic sidebar rows for every in-flight NEW chat, until each is in the
   *  saved list. `title` null = its title is still being generated. */
  optimistics: { id: string; title: string | null; caseId: string | null }[];
  /** Ids of conversations with a turn in flight — both the server id and the
   *  client key, so a brand-new chat's row spins before its id exists. */
  workingIds: Set<string>;
  /** The sidebar id of the conversation ON SCREEN — its server id, or its client
   *  key while a brand-new chat is still waiting for one. What the nav highlights.
   *  Derived here because only this hook knows which convo is current; callers
   *  guessing it from the optimistic list picked the wrong row once two new chats
   *  were in flight. */
  currentRowId: string | null;
  /**
   * This tab was reloaded with a turn in flight whose id it never learned — the
   * reload beat the server's answer, so the URL was still "/" and there was
   * nothing to come back to.
   *
   * The chat DOES exist: the server created it before it answered. The caller
   * opens the newest unnamed conversation in the list once that list arrives —
   * see the resume effect in HomePage.
   */
  resumeUnnamed: boolean;
  /** A conversation this tab had a turn in flight for when it was reloaded, if
   *  we knew its id. The caller navigates there; loading it restores the
   *  spinner (the server reports the turn as still running). Read once on
   *  mount, so it is stable and safe as an effect dep. */
  resumeId: string | null;
  /** The result tab the interrupted send had opened, to restore alongside it. */
  resumeTab: string | null;
  /** Conversations that have no server id yet, by client key. Their sidebar row
   *  is visible (that is the point — the turn is running) but there is no URL
   *  for it: navigating to a client key writes a string into the address bar
   *  that 404s on the next refresh. Callers must not route to these. */
  pendingIds: Set<string>;
  /** The viewed chat's agent, or the one the NEXT new chat will start on. */
  mode: AgentMode;
  /** The agent a NEW chat would start on, ignoring whatever chat is open. What a
   *  `forceNew` send will use — so it is what a picker on the insights canvas
   *  must show, since `mode` there is the open chat's fixed agent. */
  pendingMode: AgentMode;
  /** Pick the agent for the next new chat. Ignored once a chat is open — a
   *  chat's agent is fixed (`modeLocked`). */
  setMode: (mode: AgentMode) => void;
  /** True while a chat is open: its agent can no longer be changed. */
  modeLocked: boolean;
  /** `forceNew` sends into a brand-new chat even when one is open — the insights
   *  screen's "Ask Elliot", which must not land in whatever chat was last read. */
  /** `insightId` / `card` name the CARD a question is about — one key each,
   *  resolved server-side under the verified account. The insight came first and
   *  keeps its own slot; every place's card (Brand impersonation, Attack surface)
   *  is the one `Card` object, which names the body field it travels in. */
  send: (text: string, files?: File[], caseId?: string | null, insightId?: string | null,
         forceNew?: boolean, card?: Card | null) => void;
  /** The conversation on screen, with the gate's offers composed back in — this
   *  is what the view renders, not `messages`. See `rowsWithOffers`. */
  rows: (ChatMessage | OfferRow)[];
  /** Lookups offered and not yet sent. Callers need this to decide what a
   *  message MEANS before sending it (see `sendIntent`). */
  staged: Staged[];
  /** Ask before spending a turn: stage the lookup and put the question on
   *  screen. Runs no stream and creates no session. */
  offer: (query: string, kind: LookupKind, caseId?: string | null, forceNew?: boolean) => void;
  /** Open a chat with no turn and nothing staged — a detection link's chat. The
   *  same create as `offer`'s, named by `title`, saved with `detectionId`. */
  openConversation: (title: string, detectionId: string) => void;
  /** Yes — run the live offer's lookup, carrying any other staged one as
   *  context. No-op when nothing is live. */
  confirmOffer: () => void;
  /** No — drop the live offer's buttons, keeping it on screen as context. */
  declineOffer: () => void;
  stop: () => void;
  /** Drop the optimistic flag once the chat is in the saved list (no flash). */
  markSaved: (serverId: string) => void;
  /** `mode` comes from the saved session's metadata (SessionSummary.mode). */
  loadSession: (serverId: string, mode?: string | null) => void;
  reset: () => void;
  /** Right-panel tabs ("kind:value" keys) for the conversation on screen, in order.
   *  Which one is VISIBLE isn't here — the URL owns that (`?tab=`). */
  targets: string[];
  openTarget: (target: string) => void;
  closeTarget: (target: string) => void;
  /** Close every tab — i.e. close the panel. Deliberately not a loop over
   *  `closeTarget`; see the reducer's `closeAllTargets` case. */
  closeAllTargets: () => void;
  /** Rename a tab's target key in place (e.g. an edited AirQL query): updates the
   *  outer tab label and refetches. */
  editTarget: (oldTarget: string, newTarget: string) => void;
}

/** Build the note we append to the user's message so the agent knows where its
 *  uploaded files are mounted. The backend strips it when reconstructing
 *  history, so the user only ever sees their original text. */
function attachmentNote(uploaded: { filename: string; mount_path: string }[]): string {
  const list = uploaded.map((f) => `- ${f.filename} → ${f.mount_path}`).join('\n');
  return (
    `\n\nThe user attached ${uploaded.length} file(s) to this session (read-only):\n${list}` +
    `\n\nRead each file at the exact path shown with your file tools (read / bash). ` +
    `If a path is missing, locate it with \`find /mnt/session -name '<filename>'\` instead of guessing.`
  );
}

/**
 * Owns every conversation's state in one store, keyed by a stable client key.
 * `onSessionCreated` fires when a turn ends (so the sidebar refreshes + picks up
 * the titled session).
 */
export function useChat(onSessionCreated?: () => void): UseChat {
  const [state, dispatch] = useReducer(reducer, { convos: {}, currentKey: null });
  const stateRef = useRef(state);
  stateRef.current = state; // always-current snapshot for callbacks/handlers
  const abortMap = useRef<Map<string, AbortController>>(new Map());
  // What the Stop button needs to know about each convo's running turn, in ONE
  // record so ending a turn is one delete (three parallel maps had to be kept in
  // step by hand, and a missed one is how a stale Stop outlived its turn):
  //  - turnId: the server's id for the turn (`turn_started`). Stop sends it back
  //    so the server only ever stops THAT turn.
  //  - sid: the session id as the STREAM learned it. The `session` and
  //    `turn_started` frames arrive back to back, faster than a re-render
  //    publishes the first into stateRef; a Stop clicked in that gap read a null
  //    id from the store and was held for good. Read it from here first.
  //  - held: Stop was pressed before the id arrived (see stopPlan). The stream
  //    keeps being read, silently, and the Stop goes out on the id.
  const turns = useRef<Map<string, { turnId?: string; sid?: string; held?: boolean }>>(new Map());

  // The agent the next NEW chat will start on. Kept out of the convo store
  // because it belongs to no conversation yet; once a convo exists it carries
  // its own immutable `mode`. Mirrored into a ref so `send` doesn't have to be
  // re-created (and re-render every consumer) on each pick.
  const [pendingMode, setPendingMode] = useState<AgentMode>(DEFAULT_AGENT_MODE);
  const pendingModeRef = useRef(pendingMode);
  pendingModeRef.current = pendingMode;

  // One poll timer per conversation, keyed so re-entering a chat (or a second
  // render) can't stack them onto the same turn.
  const pollers = useRef<Map<string, number>>(new Map());
  useEffect(() => () => {
    for (const id of pollers.current.values()) window.clearTimeout(id);
    pollers.current.clear();
  }, []);

  // Last time we heard ANY bytes on a convo's stream (a frame or a keep-alive),
  // keyed by convo key. The watchdog below reads this to tell a dead stream from
  // a quiet one. See `isStuckWorking`.
  const lastSeen = useRef<Record<string, number>>({});

  /**
   * Follow a turn we are not streaming, by re-reading the transcript until the
   * server says the session has stopped running.
   *
   * This is what makes a refresh survivable. The turn runs on the backend, off
   * the request that started it, so the answer lands whether or not this browser
   * is listening — but before this, a reload mid-turn showed the question with
   * nothing under it and no spinner, because the transcript alone cannot tell
   * "still working" from "the agent said nothing". Now the server reports
   * `running` with the transcript and we simply wait it out.
   *
   * Also covers the second tab, and our own stream dying (`onInterrupted`).
   */
  // `note` is the line shown while following, in place of "Still working…". A
  // failed Stop needs its own: the generic line, written over it by the first
  // poll, wiped the only explanation of why the turn was still running.
  const watchTurn = useCallback((key: string, serverId: string, note: string = RESUMED_STATUS) => {
    if (pollers.current.has(key)) return; // already following this one
    let ticks = 0;

    const tick = () => {
      pollers.current.delete(key);
      void getSessionMessages(serverId)
        .then(({ messages, running }) => {
          // The convo is gone, or a live stream took it over while we waited —
          // the stream is better informed (it narrates status), so it wins.
          if (!stateRef.current.convos[key] || abortMap.current.has(key)) return;
          dispatch({
            t: 'patch',
            key,
            patch: {
              messages,
              phase: running ? 'working' : 'idle',
              status: running ? note : null,
              error: null,
              errorKind: null,
            },
          });
          if (running) schedule();
          // Settled: the sidebar can pick up the title the turn generated.
          else onSessionCreated?.();
        })
        .catch(() => {
          // A blip while polling says nothing about the turn — keep waiting.
          if (stateRef.current.convos[key]) schedule();
        });
    };

    const schedule = () => {
      if ((ticks += 1) > POLL_MAX_TICKS) {
        dispatch({
          t: 'patch',
          key,
          patch: {
            phase: 'error',
            status: null,
            error: 'This turn is taking longer than expected. Reload to check on it.',
            errorKind: null,
          },
        });
        return;
      }
      pollers.current.set(key, window.setTimeout(tick, POLL_MS));
    };

    schedule();
  }, [onSessionCreated]);

  /**
   * Send a Stop to the server. On failure the agent is still running, so the
   * chat must not keep claiming "stopped": it says so, then follows the turn by
   * polling — the same road a reload takes — until the answer lands.
   *
   * The explanation rides as the turn's STATUS, not as an error. It used to be
   * an error, and the first poll tick (4s later) reset `error` and painted
   * "Still working…" over it — the user was left with a spinner and no idea why
   * Stop had done nothing. As the status it is the line the poll keeps showing,
   * and 'working' is also the honest phase: the turn IS running, so the
   * composer stays shut instead of inviting a question into a busy chat.
   */
  const sendStop = useCallback((key: string, sid: string, turnId: string | null) => {
    interruptTurn(sid, turnId).catch(() => {
      dispatch({
        t: 'patch',
        key,
        patch: { phase: 'working', status: STOP_FAILED_STATUS, error: null, errorKind: null },
      });
      watchTurn(key, sid, STOP_FAILED_STATUS);
    });
  }, [watchTurn]);

  // Self-heal a turn whose stream died silently. A live turn keeps `lastSeen`
  // fresh (heartbeat ≤20s); one that has gone quiet past STUCK_SILENCE_MS has lost
  // its stream and would otherwise sit 'working' forever. Release the dead
  // connection and hand it to watchTurn, which reconciles from the transcript —
  // the same path a reload takes. Never touches a turn already being polled, and
  // never fires while the stream is still alive, so it cannot disturb a live turn;
  // and even if it ever misjudged, the backend runs the turn off the response, so
  // the answer is read back from the transcript rather than lost.
  useEffect(() => {
    const id = window.setInterval(() => {
      const now = Date.now();
      for (const [key, c] of Object.entries(stateRef.current.convos)) {
        if (pollers.current.has(key)) continue;
        if (!isStuckWorking(c, lastSeen.current[key], now)) continue;
        abortMap.current.get(key)?.abort();
        abortMap.current.delete(key);
        const held = turns.current.get(key)?.held;
        turns.current.delete(key); // that stream's turn id dies with it
        if (held) {
          // The user had already pressed Stop and it was waiting for this stream
          // to name the turn. The stream is gone, the turn isn't: stop whatever
          // holds the chat rather than follow a turn the user asked to stop.
          sendStop(key, c.serverId!, null);
          dispatch({ t: 'patch', key, patch: { phase: 'stopped', status: null } });
          continue;
        }
        watchTurn(key, c.serverId!);
      }
    }, STUCK_CHECK_MS);
    return () => window.clearInterval(id);
  }, [watchTurn, sendStop]);

  const fetchInto = useCallback((key: string, serverId: string) => {
    void getSessionMessages(serverId)
      .then(({ messages, running }) => {
        // `running` is the whole point of loading through here: opening a chat
        // whose turn is still in flight has to look like a turn in flight.
        dispatch({
          t: 'patch',
          key,
          patch: {
            messages,
            phase: running ? 'working' : 'idle',
            status: running ? RESUMED_STATUS : null,
          },
        });
        if (running) watchTurn(key, serverId);
      })
      .catch((e: unknown) =>
        dispatch({
          t: 'patch',
          key,
          patch: {
            phase: 'error',
            error: e instanceof Error ? e.message : 'Failed to load conversation',
            errorKind: null,
          },
        }),
      );
  }, [watchTurn]);

  /**
   * Send a turn. Two knobs the public `send` never needs, both for the gate:
   *
   * `echo: false` suppresses the user bubble. A confirmation is a button press,
   * not a message — the bubble it answers is already on screen, and the server
   * transcript will hold the indicator we send, so echoing would put a row on
   * screen that vanishes on the next reload.
   *
   * `note` is appended to the WIRE text only, never to what the user sees. The
   * upload note already works exactly this way (`attachmentNote` below).
   */

  const runSend = useCallback(
    (text: string, files: File[] = [], caseId: string | null = null, insightId: string | null = null,
     forceNew = false, opts: { echo?: boolean; note?: string } = {},
     card: Card | null = null) => {
      const t = text.trim();
      if (!t && files.length === 0) return;
      const cur = stateRef.current.currentKey
        ? stateRef.current.convos[stateRef.current.currentKey]
        : null;
      // Don't double-send the same chat — but `forceNew` isn't touching that chat,
      // so a busy one must not silently swallow an insight question.
      // A convo with a stream still open is busy even when its phase says
      // otherwise — a Stop being held keeps reading its stream — and a second
      // turn on it is the double-loop VIS-580 was about.
      if (!forceNew && (cur?.phase === 'working' || (cur && abortMap.current.has(cur.key)))) return;

      // "New" = no current convo at all. A convo that exists but has no serverId
      // yet (optimistic first turn, or a prior turn that errored before the server
      // assigned a session) is NOT new — reuse it so a second send (e.g. editing a
      // `/search` tab's query) appends to the same chat instead of spawning a fresh
      // empty convo, which would switch away and close the results panel.
      //
      // `forceNew` overrides that: `reset()` can't be used from the caller, because
      // it dispatches and `stateRef` only catches up on the next render — so a
      // caller that reset and sent in one go would still read the stale currentKey
      // here and append to the chat it meant to leave.
      const isNew = !cur || forceNew;
      const key = isNew ? nextKey() : cur.key;
      // A new send is a new intent. A Stop still held from the last attempt (its
      // upload failed, or the watchdog took its stream) has nothing left to stop,
      // and left in place it would interrupt THIS question the moment it starts.
      turns.current.delete(key);
      // A new chat takes the picked agent; a follow-up keeps the one its session
      // was created with (the server would ignore a change anyway).
      const mode = isNew ? pendingModeRef.current : cur.mode;
      const ctrl = new AbortController();
      abortMap.current.set(key, ctrl);
      // Is this turn the one that CREATES the session? Not the same question as
      // "is the convo new": the lookup gate opens a conversation with no session
      // at all (an offer runs no turn), so a confirmation lands on a convo that
      // already exists and still has no id. Keying the resume note on newness
      // instead left exactly that turn unrecoverable — a reload during it found
      // no note and stranded the user on the composer.
      const creating = isNew || cur!.serverId == null;
      // Written BEFORE anything leaves the browser, so the window this covers —
      // a refresh before the server has told us the session id — is closed from
      // the first instant. Only the newest send is remembered; two overlapping
      // new chats is a rare enough case that the second one wins.
      //
      // Carry the convo's OPEN result tab, if any. A confirmed lookup landed on a
      // convo the offer already opened a window on (gate flow), and a bare
      // `writePendingTurn(null)` here erased that tab from the note — so a fast
      // refresh between confirm and the turn's `session` frame lost the window.
      // The onSession callback below re-notes it once the id lands, but a refresh
      // can beat that frame; this closes the gap from the first instant.
      if (creating) {
        const openTab = stateRef.current.convos[key]?.targets.slice(-1)[0] ?? null;
        writePendingTurn(null, openTab);
      }

      // Optimistic user bubble — shown immediately, staged file chips swapped for
      // real (downloadable) ones once the upload finishes.
      const userMsgId = nextId('u');
      const userMsg: ChatMessage = {
        id: userMsgId,
        role: 'user',
        text: t,
        attachments: files.map((f) => ({ name: f.name, size_bytes: f.size, downloadable: false })),
        timestamp: Date.now(),
      };

      if (isNew) {
        dispatch({
          t: 'open',
          convo: {
            key,
            serverId: null,
            isNew: true,
            caseId,
            mode,
            phase: 'working',
            status: 'Thinking…',
            title: null,
            error: null,
            errorKind: null,
            messages: opts.echo === false ? [] : [userMsg],
            liveThoughts: [],
            staged: [],
            targets: [],
          },
        });
      } else {
        if (opts.echo !== false) dispatch({ t: 'appendMsg', key, message: userMsg });
        dispatch({
          t: 'patch',
          key,
          patch: {
            phase: 'working',
            status: 'Thinking…',
            error: null,
            errorKind: null,
            liveThoughts: [],
            // A convo with no session yet needs the optimistic sidebar row: it is
            // about to create one, and until the id lands nothing else can put a
            // row on screen. Before the gate, only `send` could open such a convo
            // and it set the flag at creation; an OFFER opens one too, and its
            // first turn comes later.
            ...(creating ? { isNew: true } : {}),
          },
        });
        dispatch({ t: 'view', key });
      }
      // Everything the gate was holding has now been conveyed — as this message
      // or inside its note — so none of it is answerable or repeatable any more.
      // It stays on screen: it is the history of this conversation. A `forceNew`
      // send is a different chat and leaves its offers alone.
      if (!isNew) dispatch({ t: 'markOffersSent', key });

      const finishError = (msg: string, kind?: 'model_outage') => {
        abortMap.current.delete(key);
        clearPendingTurn(); // nothing in flight to resume
        dispatch({
          t: 'patch',
          key,
          patch: { phase: 'error', status: null, error: msg, errorKind: kind ?? null },
        });
        onSessionCreated?.();
      };

      // Stream a turn. Every callback targets THIS convo by `key`, so it updates
      // correctly even if the user is looking at another chat.
      const runTurn = (sid: string | null, messageText: string) => {
        // Mark the turn alive as it starts, so the watchdog gives the stream its
        // full silence window before ever considering it dead.
        lastSeen.current[key] = Date.now();
        // A new turn starts with no id: whatever an earlier stream left behind
        // (one the watchdog abandoned, say) names a turn that is over, and a
        // Stop sent with it would be ignored while THIS turn runs on.
        // Nothing can be held yet either: a Stop pressed during an upload is
        // settled by the upload step itself, which never starts this turn.
        turns.current.set(key, sid ? { sid } : {});
        // Record what this stream learns about the turn (its session, its id)
        // without dropping what is already known — the two frames arrive
        // separately and a held Stop needs both.
        const note = (patch: { turnId?: string; sid?: string }) =>
          turns.current.set(key, { ...turns.current.get(key), ...patch });
        // A held Stop goes out once this stream has told us both what to stop
        // and where: the session and the turn. Whichever of those two frames
        // lands second sends it — narration frames don't, they carry neither.
        const releaseHeldStop = () => {
          const t = turns.current.get(key);
          if (!t?.held || !t.sid || !t.turnId) return;
          turns.current.delete(key);
          abortMap.current.get(key)?.abort();
          abortMap.current.delete(key);
          sendStop(key, t.sid, t.turnId);
          dispatch({ t: 'patch', key, patch: { phase: 'stopped', status: null } });
        };
        // Read at every frame, not captured once: the user can press Stop at any
        // point while this stream is being read.
        const held = () => Boolean(turns.current.get(key)?.held);
        void sendMessageStream(
          sid,
          messageText,
          mode,
          {
            signal: ctrl.signal,
            onActivity: () => {
              lastSeen.current[key] = Date.now();
            },
            onSession: (serverId) => {
              note({ sid: serverId });
              dispatch({ t: 'patch', key, patch: { serverId } });
              releaseHeldStop();
              // Now the reload has something to navigate to, not just a hint —
              // and the tab the send opened comes along (last opened wins).
              if (creating) {
                const targets = stateRef.current.convos[key]?.targets ?? [];
                writePendingTurn(serverId, targets[targets.length - 1] ?? null);
              }
            },
            // The message has reached the agent and the turn has a name. Keep it
            // for the Stop button — and if Stop was already pressed, this is the
            // moment it can finally go out.
            onTurnStarted: (turnId) => {
              note({ turnId });
              releaseHeldStop();
            },
            // While a Stop is held the chat already reads "stopped"; narration
            // that arrives in the meantime must not paint over it.
            onStatus: (s) => {
              if (held()) return;
              dispatch({ t: 'patch', key, patch: { status: s } });
            },
            onThought: (thought) => {
              if (held()) return;
              dispatch({ t: 'addThought', key, thought });
            },
            onTitle: (title) => dispatch({ t: 'patch', key, patch: { title } }),
            onError: (msg, kind) => {
              const wasHeld = held();
              turns.current.delete(key);
              // The user already stopped this one; an error now (a busy chat, say)
              // is about a turn they have walked away from.
              if (wasHeld) {
                abortMap.current.delete(key);
                clearPendingTurn(); // nothing in flight to resume
                dispatch({ t: 'patch', key, patch: { phase: 'stopped', status: null } });
                return;
              }
              finishError(msg, kind);
            },
            // The connection went, not the turn — the backend runs it off the
            // response. Reporting this as an error is how "the connection closed
            // before the response finished" ended up on top of turns that were
            // completing normally; go and read the transcript instead.
            onInterrupted: () => {
              abortMap.current.delete(key);
              const t = turns.current.get(key);
              turns.current.delete(key);
              if (t?.held && t.sid) {
                // The stream went before the id came, but the turn lives on the
                // server — stop whatever holds this chat rather than lose the Stop.
                sendStop(key, t.sid, null);
                dispatch({ t: 'patch', key, patch: { phase: 'stopped', status: null } });
                return;
              }
              if (t?.held) {
                // The stream went before even the SESSION id came: there is nothing
                // to address a Stop to, and the turn is running on the server. This
                // used to say "Response stopped" anyway — nothing was sent, the
                // agent ran to the end, and a reload brought the chat back
                // 'working'. Say what happened instead, and leave the pending-turn
                // note alone: it is how the reload finds this chat, where the turn
                // can be followed — and stopped — for real.
                dispatch({
                  t: 'patch',
                  key,
                  patch: {
                    phase: 'error',
                    status: null,
                    error: "Couldn't stop Elliot — the connection dropped before the chat was created. Reload to find it and stop it there.",
                    errorKind: null,
                  },
                });
                onSessionCreated?.();
                return;
              }
              const live = stateRef.current.convos[key];
              const sid = live?.serverId ?? null;
              if (!sid) {
                // A stream that dies before the id is, in practice, THIS PAGE
                // UNLOADING: F5 kills the fetch, and this handler runs in the old
                // document's dying moments. `finishError` here cleared the
                // pending-turn record — the exact note the reloaded page needs to
                // find its way back — so writing it before the send was pointless:
                // we deleted it ourselves ~100ms later, every time. Leave the
                // record alone (its TTL handles a genuine dead drop) and only
                // paint the error, which on a real unload nobody ever sees.
                dispatch({
                  t: 'patch',
                  key,
                  patch: {
                    phase: 'error',
                    status: null,
                    error: 'The connection dropped before the chat was created.',
                    errorKind: null,
                  },
                });
                onSessionCreated?.();
                return;
              }
              dispatch({ t: 'patch', key, patch: { status: RESUMED_STATUS, error: null } });
              watchTurn(key, sid);
            },
            onDone: (frame) => {
              abortMap.current.delete(key);
              // A Stop still held here never went out: the turn finished first.
              // Its answer is shown like any other. It used to be dropped for a
              // "Response stopped" note — but nothing was stopped (the work was
              // done and paid for), and a reload showed the answer anyway, so the
              // same chat told two different stories before and after F5.
              turns.current.delete(key);
              clearPendingTurn(); // the answer is on screen; nothing to resume
              if (frame.session_id) dispatch({ t: 'patch', key, patch: { serverId: frame.session_id } });
              const generated: Attachment[] = (frame.generated_files ?? []).map((g) => ({
                name: g.filename,
                file_id: g.file_id,
                size_bytes: g.size_bytes,
                downloadable: true,
              }));
              const thoughts = stateRef.current.convos[key]?.liveThoughts ?? [];
              const cards = intelCardsOf(frame);
              if (frame.answer || generated.length || cards.length) {
                dispatch({
                  t: 'appendMsg',
                  key,
                  message: {
                    id: nextId('a'),
                    role: 'agent',
                    text: frame.answer,
                    visualization: frame.visualization ?? null,
                    intel_cards: cards,
                    attachments: generated,
                    thoughts, // the narration that led here, collapsed above the answer
                    timestamp: Date.now(),
                  },
                });
              }
              // Buffer folded into the bubble (or discarded with an empty turn).
              dispatch({ t: 'patch', key, patch: { phase: 'idle', status: null, liveThoughts: [] } });
              onSessionCreated?.();
            },
          },
          caseId,
          insightId,
          card,
        );
      };

      // What goes on the wire: the user's words plus any hidden note. The bubble
      // above keeps `t` alone, and the server strips the note back off when it
      // reconstructs the transcript, so the two agree after a reload too.
      const wire = t + (opts.note ?? '');

      if (files.length === 0) {
        runTurn(isNew ? null : cur!.serverId, wire);
        return;
      }

      // Stop pressed while the files were still uploading: it was held, because
      // no turn existed to stop. When the upload then settles — either way — there
      // is still no turn, so honour the Stop here instead of starting one just to
      // interrupt it (or reporting an upload failure the user walked away from).
      const stoppedDuringUpload = () => {
        if (!turns.current.get(key)?.held) return false;
        turns.current.delete(key);
        abortMap.current.delete(key);
        clearPendingTurn(); // nothing in flight to resume
        dispatch({ t: 'patch', key, patch: { phase: 'stopped', status: null } });
        return true;
      };

      // Upload first, then send the message referencing the mounted files.
      dispatch({ t: 'patch', key, patch: { status: `Uploading ${files.length} file(s)…` } });
      void uploadFiles(files, isNew ? null : cur!.serverId)
        .then(({ session_id, files: uploaded }) => {
          dispatch({ t: 'patch', key, patch: { serverId: session_id } });
          const real: Attachment[] = uploaded.map((f) => ({
            name: f.filename,
            file_id: f.file_id,
            size_bytes: f.size_bytes,
            downloadable: true,
          }));
          dispatch({ t: 'patchMsg', key, id: userMsgId, patch: { attachments: real } });
          if (stoppedDuringUpload()) return;
          runTurn(session_id, wire + attachmentNote(uploaded));
        })
        .catch((e: unknown) => {
          if (stoppedDuringUpload()) return;
          finishError(e instanceof Error ? e.message : 'File upload failed.');
        });
    },
    [onSessionCreated, watchTurn, sendStop],
  );

  const send = useCallback(
    (text: string, files?: File[], caseId?: string | null, insightId?: string | null,
     forceNew?: boolean, card?: Card | null) => {
      // Everything the gate is holding rides along as context. `forceNew` opens a
      // different chat, so this one's offers are not its business.
      const open = stateRef.current.currentKey
        ? stateRef.current.convos[stateRef.current.currentKey]
        : null;
      // Everything not yet conveyed rides along as context; `lookupNote` drops
      // the exchanges the agent has already been told about.
      const carry = forceNew ? NO_OFFERS : open?.staged ?? NO_OFFERS;
      runSend(text, files, caseId, insightId, forceNew, { note: lookupNote(carry) },
              card ?? null);
    },
    [runSend],
  );

  /**
   * Ask before spending an agent turn: stage the lookup and put the question on
   * screen. No stream, no session, no cost — the result panel the caller opens
   * alongside this is what actually answers, in well under a second.
   */
  const offer = useCallback((query: string, kind: LookupKind, caseId: string | null = null,
                             forceNew = false) => {
    // `forceNew` opens a fresh chat even when one is on screen — an insight search
    // gates into its OWN conversation, never staging its offer into the chat that
    // happened to be last read. Same reason `send`'s `isNew` honours it.
    const cur = forceNew
      ? null
      : stateRef.current.currentKey
      ? stateRef.current.convos[stateRef.current.currentKey]
      : null;
    // No user bubble is appended: the offer row renders the whole exchange, both
    // bubbles, because neither of them is a message (see `rowsWithOffers`). That
    // is also what makes a confirmed offer look right — the pair disappears with
    // the drain and `send` puts the real, sent message in its place.
    if (cur) {
      dispatch({
        t: 'addOffer',
        key: cur.key,
        // Its place in the flow, as a count of real messages — see `Staged.at`.
        // `seed` is drawn HERE and nowhere else: once per offer, at the one
        // moment there is no render to re-roll it — see `offerSchedule`.
        offer: { query, kind, at: cur.messages.length, seed: Math.random() },
      });
      dispatch({ t: 'view', key: cur.key });
      return;
    }
    const key = nextKey();
    dispatch({
      t: 'open',
      convo: {
        key,
        serverId: null,
        // Optimistic, exactly like a first turn's: the row is on screen from the
        // moment the user sends, name shimmering, and `markSaved` drops the flag
        // once the real row arrives in the list.
        isNew: true,
        caseId,
        mode: pendingModeRef.current,
        phase: 'idle',
        status: null,
        title: null,
        error: null,
        errorKind: null,
        messages: [],
        liveThoughts: [],
        staged: [{ query, kind, at: 0, seed: Math.random() }],
        targets: [],
      },
    });
    // The conversation is REAL from here, turn or no turn — only the agent is
    // held back. So it is created and titled now, and everything that hangs off
    // having an id behaves as it always did: the sidebar row and its shimmer, the
    // generated name, `/chat/<id>` in the URL, and therefore surviving a refresh.
    // Written before the request goes out, for the reason `send` writes it there:
    // a refresh inside this round trip needs something to come back through.
    writePendingTurn(null);
    void openSession(query, pendingModeRef.current, caseId)
      .then((serverId) => {
        dispatch({ t: 'patch', key, patch: { serverId } });
        const targets = stateRef.current.convos[key]?.targets ?? [];
        writePendingTurn(serverId, targets[targets.length - 1] ?? null);
        // Deliberately does NOT refresh the sidebar here. That refresh flips the
        // list's `loading`, and HomePage's URL→conversation effect re-runs on it
        // — at a moment when the id has landed but the URL has not caught up
        // yet, so a bare "/" reads as "the user left the chat" and it resets the
        // view. Typing an indicator threw you out of your own conversation a
        // second later. The row is already on screen (optimistic), and the
        // sidebar's own shimmer poll fetches the generated name.
      })
      .catch(() => {
        // Deliberately does NOT clear the resume note. A create that rejects is,
        // in practice, THIS PAGE UNLOADING — F5 kills the fetch and this handler
        // runs in the old document's dying moments — and clearing the note there
        // deletes the one record the reloaded page needs to find its way back.
        // (The same trap `onInterrupted` documents; caught here by the reload
        // stories.) A genuine failure is covered by the note's TTL, and the offer
        // is still on screen: confirming re-tries the create as part of the turn,
        // because `send` sends with a null session id.
      });
  }, []);

  /**
   * A chat with no turn and no offer: what a detection link opens the moment the
   * detection is found. It is `offer`'s create without the offer — the chat is
   * real (sidebar row, generated name, `/chat/<id>`, a panel to open tabs into)
   * and the agent is not involved until the user asks something. Named from the
   * detection's title; the server saves `detectionId` on it, so the chat is about
   * that detection however it is reopened.
   */
  const openConversation = useCallback((title: string, detectionId: string) => {
    const key = nextKey();
    dispatch({
      t: 'open',
      convo: {
        key,
        serverId: null,
        isNew: true,
        caseId: null,
        mode: pendingModeRef.current,
        phase: 'idle',
        status: null,
        title: null,
        error: null,
        errorKind: null,
        messages: [],
        liveThoughts: [],
        staged: [],
        targets: [],
      },
    });
    // Same resume note as `offer`'s, for the same reason: a refresh inside the
    // round trip needs something to come back through.
    writePendingTurn(null);
    void openSession(title, pendingModeRef.current, null, detectionId)
      .then((serverId) => {
        dispatch({ t: 'patch', key, patch: { serverId } });
        const targets = stateRef.current.convos[key]?.targets ?? [];
        writePendingTurn(serverId, targets[targets.length - 1] ?? null);
      })
      // Kept, as `offer` keeps it: a rejection here is usually this page unloading.
      .catch(() => {});
  }, []);

  /**
   * Yes: run the lookup we offered. The message is the indicator alone — exactly
   * what `/ip` sent before the gate existed, so the agent's own behaviour is
   * unchanged — and any OTHER staged offer travels as the note, because it is
   * context the agent has never seen.
   */
  const confirmOffer = useCallback(() => {
    const cur = stateRef.current.currentKey
      ? stateRef.current.convos[stateRef.current.currentKey]
      : null;
    if (!cur) return;
    const at = liveOfferAt(cur.staged);
    if (at === -1) return;
    const answered = cur.staged[at];
    const others = cur.staged.filter((_, i) => i !== at);
    // It stops being an offer and becomes the turn — see the reducer's dropOffer.
    dispatch({ t: 'dropOffer', key: cur.key, index: at });
    runSend(answered.query, [], cur.caseId, null, false, {
      // `confirmed`: the message is the lookup they chose, NOT an answer in words.
      // Saying "their reply" about a bare IP invited reading it as a correction
      // of one of the exchanges listed below it.
      note: lookupNote(others, { confirmed: true }),
    });
  }, [runSend]);

  /** No: drop the buttons. The exchange stays on screen and in `staged`, so a
   *  later question still reaches the agent knowing it happened. */
  const declineOffer = useCallback(() => {
    const k = stateRef.current.currentKey;
    if (k) dispatch({ t: 'declineOffer', key: k });
  }, []);

  /**
   * Stop the viewed chat's turn — on the SERVER, not just here.
   *
   * It used to stop only the listening: the agent ran on and cost what it cost,
   * and the composer opened up while it did, so the next question started a
   * second turn on a busy session (VIS-580). Now the server is told — by turn
   * id when we have it, untargeted when we are only polling (see stopPlan) — and
   * the chat reads "stopped" at once. Before the id has arrived the Stop is held
   * instead: the chat says "Stopping…" and stays 'working' (composer shut) until
   * the id lands and the Stop goes out.
   * The poll timer is dropped either way, so a stopped chat doesn't quietly
   * re-attach four seconds later.
   *
   * Refresh, tab close and a dropped connection never come through here: those
   * keep the turn running by design (the answer is waiting when you come back).
   */
  const stop = useCallback(() => {
    const k = stateRef.current.currentKey;
    if (!k) return;
    const t = turns.current.get(k);
    const sid = t?.sid ?? stateRef.current.convos[k]?.serverId ?? null;
    const turnId = t?.turnId ?? null;
    const plan = stopPlan({ turnId, streaming: abortMap.current.has(k), serverId: sid });
    if (plan === 'hold') {
      // The stream stays open and its next frames send the Stop. Until then the
      // turn is still alive, so the chat says so — and stays 'working', which
      // keeps the composer shut: a question sent now would land on a busy turn.
      turns.current.set(k, { ...t, held: true });
      dispatch({ t: 'patch', key: k, patch: { status: 'Stopping…' } });
      return;
    }
    abortMap.current.get(k)?.abort(); // stops consuming the stream for THIS chat
    abortMap.current.delete(k);
    turns.current.delete(k);
    if (sid && plan !== 'none') sendStop(k, sid, plan === 'send' ? turnId : null);
    const poll = pollers.current.get(k);
    if (poll !== undefined) {
      window.clearTimeout(poll);
      pollers.current.delete(k);
    }
    dispatch({ t: 'patch', key: k, patch: { phase: 'stopped', status: null } });
  }, [sendStop]);

  const loadSession = useCallback(
    (id: string, mode?: string | null) => {
      // `id` is a saved session id, or a not-yet-saved convo's client key. Reuse
      // the convo if we have it (live turn or cached history) — its store state is
      // the source of truth, so no disruptive mid-turn re-fetch.
      const serverId = id;
      const existing = Object.values(stateRef.current.convos).find(
        (c) => c.serverId === id || c.key === id,
      );
      if (existing) {
        dispatch({ t: 'view', key: existing.key });
        // `id` can be a CLIENT KEY here — that is how a brand-new chat's sidebar
        // row is opened before the server has named it. Only re-fetch when it is
        // a real session id, or the retry would ask the backend for a key it has
        // never heard of.
        if (
          existing.phase === 'loading' &&
          existing.messages.length === 0 &&
          existing.serverId === id
        ) {
          fetchInto(existing.key, serverId); // retry a failed/empty load
        }
        return;
      }
      const key = serverId;
      dispatch({
        t: 'open',
        convo: {
          key,
          serverId,
          isNew: false,
          caseId: null,
          // An older session has no mode in its metadata → it ran on Recon.
          mode: toAgentMode(mode),
          phase: 'loading',
          status: null,
          title: null,
          error: null,
          errorKind: null,
          messages: [],
          liveThoughts: [],
          // ...and any lookup it was still waiting on an answer about. The
          // transcript below replaces `messages` and never touches these, which
          // is the whole reason they are kept apart from it.
          staged: loadOffers(serverId),
          // Reopening a chat restores the tabs it was last used with.
          targets: loadLayout(serverId),
        },
      });
      fetchInto(key, serverId);
    },
    [fetchInto],
  );

  /**
   * A turn this tab had in flight when it was reloaded, if any — read once, on
   * mount. HomePage navigates to it; the URL effect then loads it, and the load
   * sees `running` and puts the spinner back.
   *
   * `null` here means either "nothing was in flight" or "something was, but we
   * never learned its id" — the second case still refreshes the sidebar below,
   * because the chat DOES exist server-side and has to be findable.
   */
  // Read pure, clear in the effect below. The clear used to live in this
  // initializer, and the app mounts under StrictMode, which double-runs
  // initializers in dev — a read-and-delete there self-destructs: the first run
  // clears the record and the rerun gets null. Hygiene either way: an
  // initializer is a computation, not a place for side effects.
  const [pendingOnBoot] = useState(readPendingTurn);
  const resumeId = pendingOnBoot?.id ?? null;
  // The result tab the interrupted send had opened, if any — the resume puts it
  // back into the URL, because no other record of it survives a fast refresh.
  const resumeTab = pendingOnBoot?.tab ?? null;
  // A send was in flight whose id never reached us — the reload beat the server's
  // answer. Nothing here can name that chat, but it exists and the caller can
  // find it (see `resumeUnnamed`).
  const resumeUnnamed = pendingOnBoot != null && pendingOnBoot.id == null;
  useEffect(() => {
    // The pending record is NOT cleared here. Clearing on boot burned it before
    // anything was recovered — so an impatient second F5, landing while the
    // first recovery was still waiting for the sessions list, found nothing and
    // stranded the user. The record is consumed where the resume actually
    // CONCLUDES (HomePage: navigated, gave up, or the URL already knew better);
    // its TTL covers a tab that never concludes at all.
    //
    // The list fetched at boot can predate the session, so ask again — otherwise
    // the user is looking at a sidebar that doesn't contain the chat they just
    // started.
    if (resumeUnnamed) onSessionCreated?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markSaved = useCallback((serverId: string) => dispatch({ t: 'markSaved', serverId }), []);
  const reset = useCallback(() => dispatch({ t: 'view', key: null }), []);
  const openTarget = useCallback((target: string) => dispatch({ t: 'openTarget', target }), []);
  const closeTarget = useCallback((target: string) => dispatch({ t: 'closeTarget', target }), []);
  const closeAllTargets = useCallback(() => dispatch({ t: 'closeAllTargets' }), []);
  const editTarget = useCallback(
    (oldTarget: string, newTarget: string) => dispatch({ t: 'editTarget', oldTarget, newTarget }),
    [],
  );
  const cur = state.currentKey ? state.convos[state.currentKey] : null;

  // Persist the viewed chat's tab layout. Only the viewed convo's tabs can change
  // (the panel belongs to it), and the reducer keeps `targets` referentially stable
  // when it doesn't touch it — so this does NOT fire on every streaming chunk. A
  // new chat persists as soon as its serverId lands. Merely switching tabs writes
  // nothing: the active one is a URL concern.
  useEffect(() => {
    if (cur?.serverId) saveLayout(cur.serverId, cur.targets);
  }, [cur?.serverId, cur?.targets]);

  // Same rule for the gate's offers, with one difference: they are persisted for
  // an id-less convo too (under `draft`), because that is exactly the case with
  // no session and no transcript to recover from. Draining them removes the
  // entry, so a confirmed draft leaves nothing behind for the next one to find.
  useEffect(() => {
    if (cur?.serverId) saveOffers(cur.serverId, cur.staged);
  }, [cur?.serverId, cur?.staged]);

  // Every in-flight new chat, not just one. It used to be a single row picked as
  // `current ?? first`, so a second new chat — "Ask Elliot" from an insight while
  // one was already running, or New Chat mid-turn — had nowhere to appear and the
  // two fought over the same sidebar row.
  const optimistics = useMemo(
    () =>
      Object.values(state.convos)
        .filter((c) => c.isNew)
        .map((c) => ({ id: c.serverId ?? c.key, title: c.title, caseId: c.caseId })),
    [state.convos],
  );

  // Rows that exist but have no URL yet. See `pendingIds` in the return type.
  const pendingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of Object.values(state.convos)) if (!c.serverId) ids.add(c.key);
    return ids;
  }, [state.convos]);

  // Keyed by BOTH ids on purpose: the sidebar matches on the row's id, and a
  // brand-new chat's row is keyed on its client key until the server id lands.
  // Spinning only on `serverId` meant the first second of every new chat — the
  // part where the user most wants to see something happening — had no spinner
  // anywhere.
  // What the conversation actually renders: the transcript with the gate's
  // unanswered offers put back where they belong. Memoised on the two inputs, so
  // an unrelated render does not rebuild the list.
  const rows = useMemo(
    () => rowsWithOffers<ChatMessage>(cur?.messages ?? NO_MESSAGES, cur?.staged ?? NO_OFFERS),
    [cur?.messages, cur?.staged],
  );

  const workingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of Object.values(state.convos)) {
      if (c.phase !== 'working') continue;
      ids.add(c.key);
      if (c.serverId) ids.add(c.serverId);
    }
    return ids;
  }, [state.convos]);

  return {
    sessionId: cur?.serverId ?? null,
    messages: cur?.messages ?? [],
    liveThoughts: cur?.liveThoughts ?? NO_THOUGHTS,
    status: cur?.status ?? null,
    sending: cur?.phase === 'working',
    error: cur?.phase === 'error' ? cur.error : null,
    errorKind: cur?.phase === 'error' ? cur.errorKind : null,
    hasConversation: cur != null,
    loadingConversation: cur?.phase === 'loading',
    stopped: cur?.phase === 'stopped',
    optimistics,
    workingIds,
    pendingIds,
    currentRowId: cur ? cur.serverId ?? cur.key : null,
    resumeId,
    resumeTab,
    resumeUnnamed,
    mode: cur?.mode ?? pendingMode,
    pendingMode,
    setMode: setPendingMode,
    modeLocked: cur != null,
    send,
    rows,
    staged: cur?.staged ?? NO_OFFERS,
    offer,
    openConversation,
    confirmOffer,
    declineOffer,
    stop,
    markSaved,
    loadSession,
    reset,
    targets: cur?.targets ?? NO_TABS,
    openTarget,
    closeTarget,
    closeAllTargets,
    editTarget,
  };
}
